import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../utils/api';
import { exportDataToCSV } from '../../../utils/exportUtils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import Button from '../../../components/ui/Button';

const COLORS = ['#1E3A5F', '#4A90A4', '#F39C12', '#27AE60', '#E74C3C', '#9B59B6', '#34495E'];

const getCategories = (category) => {
  if (Array.isArray(category)) {
    const values = category.filter(Boolean);
    return values.length ? values : ['Uncategorized'];
  }
  return category ? [category] : ['Uncategorized'];
};

const InventoryChart = () => {
  const [activeChart, setActiveChart] = useState('trends');
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const fetchChartData = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/reports/analytics');
        if (!Array.isArray(data.products) || !Array.isArray(data.sales)) {
          throw new Error('Dashboard analytics response has an unexpected shape');
        }
        if (active) {
          setProducts(data.products);
          setSales(data.sales);
        }
      } catch (requestError) {
        console.error('Failed to fetch dashboard analytics:', requestError);
        if (active) setError('Analytics could not be loaded from the backend.');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchChartData();
    return () => {
      active = false;
    };
  }, []);

  const monthlySales = useMemo(() => {
    const currentMonth = new Date();
    const months = [];
    for (let offset = 8; offset >= 0; offset -= 1) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - offset, 1);
      months.push({
        key: `${date.getFullYear()}-${date.getMonth()}`,
        month: date.toLocaleString(undefined, { month: 'short', year: '2-digit' }),
        unitsSold: 0
      });
    }
    const monthByKey = new Map(months.map(month => [month.key, month]));
    sales.forEach(sale => {
      const date = new Date(sale.createdAt);
      const month = monthByKey.get(`${date.getFullYear()}-${date.getMonth()}`);
      if (month && Number.isFinite(Number(sale.quantity))) month.unitsSold += Number(sale.quantity);
    });
    return months;
  }, [sales]);

  const categoryData = useMemo(() => {
    const values = new Map();
    products.forEach(product => {
      const value = Number(product.quantity) * Number(product.cost);
      if (!Number.isFinite(value)) return;
      const categories = getCategories(product.category);
      categories.forEach(category => {
        const current = values.get(category) || { value: 0, count: 0 };
        current.value += value / categories.length;
        current.count += 1;
        values.set(category, current);
      });
    });
    const allValue = Array.from(values.values()).reduce((sum, item) => sum + item.value, 0);
    return Array.from(values.entries())
      .map(([name, item], index) => ({
        name,
        value: allValue > 0 ? Number(((item.value / allValue) * 100).toFixed(1)) : 0,
        count: item.count,
        color: COLORS[index % COLORS.length]
      }))
      .sort((a, b) => b.value - a.value);
  }, [products]);

  const dailySales = useMemo(() => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const values = days.map(day => ({ day, unitsSold: 0 }));
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    sales.forEach(sale => {
      const date = new Date(sale.createdAt);
      if (Number.isFinite(date.getTime()) && date.getTime() >= weekAgo && Number.isFinite(Number(sale.quantity))) {
        values[date.getDay()].unitsSold += Number(sale.quantity);
      }
    });
    return values;
  }, [sales]);

  const chartTypes = [
    { id: 'trends', label: 'Monthly Sales', icon: 'TrendingUp' },
    { id: 'categories', label: 'Inventory by Category', icon: 'PieChart' },
    { id: 'movements', label: 'Sales by Weekday', icon: 'Activity' }
  ];

  const handleExport = () => {
    if (activeChart === 'trends') {
      exportDataToCSV(['Month', 'Units Sold'], monthlySales.map(item => [item.month, item.unitsSold]), 'dashboard-monthly-sales');
    } else if (activeChart === 'categories') {
      exportDataToCSV(
        ['Category', 'Inventory Cost Value Share', 'Products'],
        categoryData.map(item => [item.name, `${item.value}%`, item.count]),
        'dashboard-inventory-by-category'
      );
    } else {
      exportDataToCSV(['Weekday', 'Units Sold'], dailySales.map(item => [item.day, item.unitsSold]), 'dashboard-sales-by-weekday');
    }
  };

  const hasData = activeChart === 'trends'
    ? monthlySales.some(item => item.unitsSold > 0)
    : activeChart === 'categories'
      ? categoryData.length > 0
      : dailySales.some(item => item.unitsSold > 0);

  const tooltip = <Tooltip />;
  const chart = activeChart === 'trends' ? (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={monthlySales}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} />
        <YAxis stroke="var(--color-muted-foreground)" fontSize={12} allowDecimals={false} />
        {tooltip}
        <Line type="monotone" dataKey="unitsSold" name="Units sold" stroke="var(--color-primary)" strokeWidth={3} />
      </LineChart>
    </ResponsiveContainer>
  ) : activeChart === 'categories' ? (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie data={categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" nameKey="name">
          {categoryData.map(item => <Cell key={item.name} fill={item.color} />)}
        </Pie>
        <Tooltip formatter={(value, _name, item) => [`${value}% (${item.payload.count} products)`, 'Cost value']} />
      </PieChart>
    </ResponsiveContainer>
  ) : (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={dailySales}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis dataKey="day" stroke="var(--color-muted-foreground)" fontSize={12} />
        <YAxis stroke="var(--color-muted-foreground)" fontSize={12} allowDecimals={false} />
        {tooltip}
        <Bar dataKey="unitsSold" name="Units sold" fill="var(--color-primary)" radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <div className="bg-card border border-border rounded-lg shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Analytics</h2>
            <p className="text-sm text-muted-foreground">Sales and current inventory data from the backend</p>
          </div>
          <Button variant="outline" iconName="Download" iconPosition="left" iconSize={16} onClick={handleExport} disabled={loading || !!error}>
            Export
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-2 sm:space-y-0">
          {chartTypes.map(item => (
            <Button
              key={item.id}
              variant={activeChart === item.id ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveChart(item.id)}
              iconName={item.icon}
              iconPosition="left"
              iconSize={16}
              className="w-full sm:w-auto"
            >
              {item.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {loading ? (
          <div className="h-[300px] flex items-center justify-center text-sm text-muted-foreground">Loading backend data...</div>
        ) : error ? (
          <div role="alert" className="h-[300px] flex items-center justify-center text-sm text-destructive">{error}</div>
        ) : !hasData ? (
          <div className="h-[300px] flex items-center justify-center text-sm text-muted-foreground">
            {activeChart === 'categories' ? 'N/A — no inventory data is available.' : '0 — no sales recorded for this chart.'}
          </div>
        ) : (
          <>
            {chart}
            {activeChart === 'categories' && (
              <div className="grid grid-cols-2 gap-3 mt-4">
                {categoryData.map(item => (
                  <div key={item.name} className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-sm text-muted-foreground">{item.name} ({item.count})</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InventoryChart;
